/**
 * AI sohbet akışı (SSE).
 *
 * Uç `text/event-stream` döndürüyor ve axios gövdeyi parça parça veremiyor; bu yüzden
 * burada `fetch` + `ReadableStream` kullanılıyor. Kimlik başlıkları axios'tan değil
 * `authHeaders()`'tan geliyor (rapor görüntüleyicide de aynı yol).
 *
 * Olay adlandırması İKİ BİÇİMDE geliyor: SSE kontrol mesajları küçük harfle
 * (`type`/`sessionId`/`quota`), Claude CLI zarfı ise PascalCase (`Type`/`Message`)
 * serileşiyor. `pick` ikisini de okur — mobil istemcide de aynı yöntem kullanılıyor.
 *
 * Masaüstü panelinden farklı olarak araç (tool_use) olayları da normalize ediliyor:
 * `Write` ile yazılan HTML raporlar kart olarak gösteriliyor, `open_screen`/`open_report`
 * çağrıları uygulamada sekme açıyor.
 *
 * Rapor dosyaları asıl olarak SUNUCUDAN gelir (`artifact` olayı, akışın sonunda): DAL çalışma
 * dizininde o turda oluşan dosyaları (node/bash ile üretilenler dahil) veritabanına alıp
 * üst bilgisini gönderir. `Write` yakalaması canlı önizleme için kalır; aynı dosya sunucudan
 * gelince store onu kimlikli olanla değiştirir.
 */
import { authHeaders } from '@/shared/api/http/tokenProvider';
import { runtimeConfig } from '@/app/config/runtimeConfig';
import { ENDPOINTS } from '@/shared/api/endpoints';
import type { AiQuota, AiReportParameter, ClaudeCodeChatRequest, ReportArtifact } from '@/shared/types/aiChat';
import { deriveTitle } from '../model/artifacts';

/** Akıştan üretilen normalize olaylar. */
export type AiStreamEvent =
  | { kind: 'session'; sessionId: string }
  /**
   * Sunucunun çözdüğü sohbet kaydı. İstemci bunu sonraki mesajlarda geri gönderir; önceden
   * bildirilmediği için her mesaj ayrı bir sohbet kaydı açıyordu.
   */
  | { kind: 'conversation'; conversationId: string }
  | { kind: 'text'; text: string }
  /**
   * CLI'ın `result` olayındaki metin: son yanıtın KOPYASI. Metin zaten aktıysa çağıran
   * bunu eklemez (aynı paragraf iki kez görünüyordu); hiç metin akmadıysa yanıt budur.
   */
  | { kind: 'result'; text: string }
  | { kind: 'tool'; tool: string }
  /** Rapor dosyası: sunucudan (kalıcı, kimlikli) ya da `Write`'tan (satır içi). */
  | { kind: 'artifact'; artifact: ReportArtifact }
  /** `open_screen` / `open_report` MCP sinyali. */
  | { kind: 'openScreen'; target: AiOpenScreenSignal }
  | { kind: 'complete'; quota?: AiQuota | null }
  | { kind: 'error'; code?: string; message: string };

export interface AiOpenScreenSignal {
  type: 'screen' | 'report';
  id: string | null;
  filterValue: string | null;
  caption: string | null;
}

type Bag = Record<string, unknown>;

/** Alanı hem camelCase hem PascalCase adıyla arar. */
function pick<T = unknown>(obj: Bag | null | undefined, ...keys: string[]): T | undefined {
  if (!obj) return undefined;
  for (const key of keys) {
    const lower = key.charAt(0).toLowerCase() + key.slice(1);
    const upper = key.charAt(0).toUpperCase() + key.slice(1);
    for (const k of [key, lower, upper]) {
      const value = obj[k];
      if (value !== undefined && value !== null) return value as T;
    }
  }
  return undefined;
}

const OPEN_SCREEN_TOOLS = /open_?screen|open_?report/i;

/** Ekran açma sinyalinin girdisi — alan adları araca göre değişebiliyor. */
function toOpenScreenSignal(toolName: string, input: Bag): AiOpenScreenSignal {
  const isReport = /report/i.test(toolName);
  const str = (...keys: string[]) => {
    for (const key of keys) {
      const value = pick<string>(input, key);
      if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return null;
  };
  return {
    type: isReport ? 'report' : 'screen',
    id: isReport ? str('reportId', 'screenId', 'objectId') : str('screenId', 'objectId', 'reportId'),
    filterValue: str('filterValue', 'filter'),
    caption: str('caption', 'title'),
  };
}

/** `Write` aracı .html yazdıysa canlı önizleme kartı çıkar (satır içi içerik). */
function toArtifact(input: Bag): ReportArtifact | null {
  const path = pick<string>(input, 'file_path', 'filePath', 'path');
  const content = pick<string>(input, 'content');
  if (!path || !content || !/\.html?$/i.test(path)) return null;
  const fileName = path.split(/[\\/]/).pop() ?? path;
  return {
    // Başlık belgenin kendi <title>/<h1>'inden; dosya adı teknik, kartta raporun adı okunmalı.
    title: deriveTitle(content) || fileName.replace(/\.html?$/i, ''),
    fileName,
    code: content,
    contentType: 'text/html',
    isText: true,
  };
}

function toParameter(raw: Bag): AiReportParameter {
  const name = pick<string>(raw, 'name') ?? '';
  return {
    name,
    caption: pick<string>(raw, 'caption') ?? name,
    type: pick<string>(raw, 'type') ?? 'text',
    defaultValue: pick<string>(raw, 'defaultValue', 'default') ?? null,
    required: pick<boolean>(raw, 'required') ?? false,
  };
}

/** Sunucunun akış sonunda gönderdiği kalıcı çıktı (AiArtifactDto, PascalCase serileşir). */
function toServerArtifact(raw: Bag | undefined): ReportArtifact | null {
  const artifactId = pick<string>(raw, 'artifactId');
  const fileName = pick<string>(raw, 'fileName');
  if (!artifactId || !fileName) return null;
  const params = pick<unknown[]>(raw, 'parameters');
  return {
    artifactId,
    fileName,
    title: pick<string>(raw, 'title') || fileName,
    contentType: pick<string>(raw, 'contentType') ?? 'application/octet-stream',
    size: Number(pick<number>(raw, 'size') ?? 0),
    isText: pick<boolean>(raw, 'isText') ?? false,
    hasParameters: pick<boolean>(raw, 'hasParameters') ?? false,
    hasQuery: pick<boolean>(raw, 'hasQuery') ?? false,
    parameters: Array.isArray(params) ? params.map((p) => toParameter(p as Bag)) : [],
  };
}

/** Tek bir tool_use içeriğinden olay üretir (yapılandırılmış ve düz biçim ortak). */
function toolEvents(toolName: string | undefined, input: Bag | undefined): AiStreamEvent[] {
  if (!toolName) return [];
  const events: AiStreamEvent[] = [];
  if (input && OPEN_SCREEN_TOOLS.test(toolName)) {
    events.push({ kind: 'openScreen', target: toOpenScreenSignal(toolName, input) });
  }
  if (input && /^write$/i.test(toolName)) {
    const artifact = toArtifact(input);
    if (artifact) events.push({ kind: 'artifact', artifact });
  }
  events.push({ kind: 'tool', tool: toolName });
  return events;
}

/** Tek bir SSE yükünü normalize olaylara çevirir. */
export function toEvents(payload: Bag): AiStreamEvent[] {
  const type = pick<string>(payload, 'type');
  if (!type) return [];

  switch (type) {
    case 'session': {
      const sessionId = pick<string>(payload, 'sessionId');
      return sessionId ? [{ kind: 'session', sessionId }] : [];
    }
    case 'conversation': {
      const conversationId = pick<string>(payload, 'conversationId');
      return conversationId ? [{ kind: 'conversation', conversationId }] : [];
    }
    case 'assistant': {
      const message = pick<Bag>(payload, 'message');
      const content = pick<unknown[]>(message, 'content');
      if (!Array.isArray(content)) return [];
      const events: AiStreamEvent[] = [];
      for (const raw of content) {
        const item = raw as Bag;
        const itemType = pick<string>(item, 'type');
        if (itemType === 'text') {
          const text = pick<string>(item, 'text');
          if (text) events.push({ kind: 'text', text });
        } else if (itemType === 'tool_use') {
          events.push(...toolEvents(pick<string>(item, 'name'), pick<Bag>(item, 'input')));
        }
      }
      return events;
    }
    case 'tool_use':
    case 'tool':
      // Düz biçim: araç adı ve girdisi kökte.
      return toolEvents(pick<string>(payload, 'toolName', 'name'), pick<Bag>(payload, 'toolInput', 'input'));
    case 'text':
    case 'content': {
      const text = pick<string>(payload, 'content', 'text');
      return text ? [{ kind: 'text', text }] : [];
    }
    case 'result': {
      // Son yanıtın kopyası: hiç metin akmadıysa yanıt budur, aktıysa çağıran yok sayar.
      const result = pick<string>(payload, 'result');
      return result ? [{ kind: 'result', text: result }] : [];
    }
    case 'artifact': {
      const artifact = toServerArtifact(pick<Bag>(payload, 'artifact'));
      return artifact ? [{ kind: 'artifact', artifact }] : [];
    }
    case 'complete':
      return [{ kind: 'complete', quota: pick<AiQuota>(payload, 'quota') ?? null }];
    case 'error':
      return [
        {
          kind: 'error',
          code: pick<string>(payload, 'code'),
          message: pick<string>(payload, 'content', 'message') ?? 'Bilinmeyen hata',
        },
      ];
    default:
      // system, tool_result, user… — arayüzde karşılığı yok.
      return [];
  }
}

export interface StreamChatOptions {
  request: ClaudeCodeChatRequest;
  isNewSession: boolean;
  signal?: AbortSignal;
  onEvent: (event: AiStreamEvent) => void;
}

/** Sohbet isteğini gönderir ve yanıtı satır satır okur. */
export async function streamChat({
  request,
  isNewSession,
  signal,
  onEvent,
}: StreamChatOptions): Promise<void> {
  const url = `${runtimeConfig().dalUrl}${isNewSession ? ENDPOINTS.AI_CHAT_STREAM_START : ENDPOINTS.AI_CHAT_STREAM_CONTINUE}`;

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', ...authHeaders() },
      body: JSON.stringify(request),
      signal,
    });
  } catch (error) {
    // Kullanıcı durdurduysa hata değil.
    if (signal?.aborted) return;
    onEvent({ kind: 'error', message: error instanceof Error ? error.message : 'Bağlantı hatası' });
    return;
  }

  if (!response.ok) {
    onEvent({ kind: 'error', message: `Sunucu hatası: ${response.status}` });
    return;
  }
  if (!response.body) {
    onEvent({ kind: 'error', message: 'Akış başlatılamadı.' });
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    for (;;) {
      if (signal?.aborted) break;
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE olayları boş satırla ayrılır; her olay bir ya da daha çok `data:` satırı taşır.
      let index: number;
      while ((index = buffer.indexOf('\n\n')) !== -1) {
        dispatch(buffer.slice(0, index), onEvent);
        buffer = buffer.slice(index + 2);
      }
    }
    if (buffer.trim()) dispatch(buffer, onEvent);
  } catch (error) {
    if (!signal?.aborted) {
      onEvent({ kind: 'error', message: error instanceof Error ? error.message : 'Akış hatası' });
    }
  } finally {
    try {
      await reader.cancel();
    } catch {
      /* akış zaten kapanmış olabilir */
    }
  }
}

function dispatch(rawEvent: string, onEvent: (event: AiStreamEvent) => void) {
  for (const line of rawEvent.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed.startsWith('data:')) continue;
    const data = trimmed.slice(5).trim();
    if (!data || data === '[DONE]') continue;
    try {
      for (const event of toEvents(JSON.parse(data) as Bag)) onEvent(event);
    } catch {
      /* parça/JSON olmayan satır — yok say */
    }
  }
}
